<ph-chat-gutter [ngSwitch]="view">

    <!-- ============================================================ -->
    <!-- LIST                                                          -->
    <!-- ============================================================ -->
    <ph-stack *ngSwitchCase="'list'">
        <!-- Search and "New skill" are both dead controls with nothing to
             search and nothing to sit alongside — the empty state below carries
             the same three actions as this toolbar's menu.
             `!loading` as well, and the same on every block below: `panelEmpty`
             is false WHILE the list is in flight, so a panel opened cold used to
             assemble its toolbar, drafts and footnote around a spinner and then
             throw all three away the moment the response said "no skills". One
             load state, then one final state. -->
        <ph-page-header *ngIf="!loading && !panelEmpty && !listLoadFailed" dense size="small" [level]="2">
            Your skills
            <ph-badge slot="title-actions" variant="lighter" color="gray" size="small">{{ skills.length }}</ph-badge>
            <ph-dropdown slot="actions" trigger-label="New skill" trigger-primary align="end" [items]="addSkillMenu" (ph-select)="onAddSkill($event.detail)"></ph-dropdown>
        </ph-page-header>

        <!-- A handful of skills is read at a glance; search earns its row once the list is long. -->
        <ph-input *ngIf="!loading && !panelEmpty && !listLoadFailed && showSearch"
                  type="text" icon="magnifying-glass" clearable placeholder="Search skills"
                  [(ngModel)]="searchTerm" ngDefaultControl aria-label="Search skills"></ph-input>

        <!-- Skeleton on FIRST load only (`!listLoaded`), and the plain spinner
             on every refetch. `loading` alone would blank the whole grid when
             a refresh refires `loadList()`. -->
        <ph-skeleton *ngIf="!listLoaded && !listLoadFailed" shape="list" rows="4" label="Loading skills"></ph-skeleton>

        <ph-toolbar *ngIf="loading && listLoaded" nowrap>
            <ph-spinner size="16"></ph-spinner> <!-- kitlint-allow-spinner: one-for-one swap of an existing spinner, refetching already-rendered content -->
            <span>Loading skills…</span>
        </ph-toolbar>

        <ph-alert *ngIf="!loading && skills.length >= 40" status="warning" variant="filled">
            {{ skills.length }} of 50 skills. Gable offers at most 50 per conversation (most-used first) —
            past the cap, the least-used stop being listed. Retire stale skills to stay under.
        </ph-alert>

        <!-- `!panelEmpty`, not just `!loading`: with no skills at all the list
             has nothing to say, and the block below owns that case.
             `!listLoadFailed` as well, and on the toolbar and helper too: a
             failed load leaves `skills` empty with `panelEmpty` false (it is
             deliberately false on failure), so all three used to assemble
             around nothing and the search-empty branch told a user whose
             request 403'd that no skills matched their search. One failure,
             one message — the error block below. -->
        <ng-container *ngIf="!loading && !panelEmpty && !listLoadFailed">
            <ng-container *ngIf="filteredSkills.length > 0; else emptyState">
                <ph-hint *ngIf="searchTerm.trim()">{{ filteredSkills.length }} of {{ skills.length }} skills</ph-hint>
                <ph-stack gap="snug">
                    <ph-list-row *ngFor="let s of filteredSkills; trackBy: trackBySkillId"
                                 variant="card"
                                 wrap
                                 [heading]="s.name"
                                 [meta]="s.description || ''"
                                 [note]="s.description ? '' : 'No description yet, so Gable can\'t match on it.'"
                                 [muted]="!s.enabled"
                                 (ph-select)="openDetail(s)">
                        <ph-status-badge slot="actions" variant="light" [status]="s.enabled ? 'completed' : 'disabled'">{{ s.enabled ? 'On' : 'Off' }}</ph-status-badge>
                        <ph-toolbar slot="detail" gap="tight">
                            <ph-tag *ngIf="isSuperUser && s.reachSummary" variant="gray" icon="buildings">{{ s.organizationId ? 'This organization' : s.reachSummary }}</ph-tag>
                            <ph-hint *ngIf="s.updatedAt">Updated {{ relativeUpdated(s.updatedAt) }}</ph-hint>
                        </ph-toolbar>
                    </ph-list-row>
                </ph-stack>
            </ng-container>
            <!-- Only reachable with skills behind the search, so this is a
                 "no match", not a "none yet" — those are different problems and
                 the first one is fixed by clearing the box. -->
            <ng-template #emptyState>
                <ph-empty-state icon="magnifying-glass" heading="No skills match your search" center></ph-empty-state>
            </ng-template>
        </ng-container>


        <!-- A failed load is not an empty list: without this the block below told
             a user with forty skills that they had none, and offered to create
             one. Mirrors the Instructions panel's retry. -->
        <ph-alert *ngIf="listLoadFailed && !loading" status="error" variant="lighter" size="small" role="alert">
            Couldn't load your skills.
            <ph-link-button variant="primary" size="small" (click)="loadList()">Try again</ph-link-button>
        </ph-alert>

        <!-- The whole empty state, once: what a skill is, and the three ways to
             make one. The explanation used to be split across a splash header, a
             "No skills yet" card and a footnote, none of which was actionable. -->
        <ph-empty-state *ngIf="panelEmpty" icon="scroll" heading="No skills yet" center>
            A skill is a standing instruction Gable pulls in when the work matches it — house style,
            a policy to quote, a checklist to follow. Skills change how Gable writes and decides,
            never what it can do without your approval.
            <ph-button slot="actions" icon="sparkle" (click)="onAddWriteWithGable()">Write one with Gable</ph-button>
            <ph-button slot="actions" variant="secondary" mode="filled" icon="books" (click)="onAddFromLibrary()">Browse the library</ph-button>
            <ph-button slot="actions" variant="secondary" mode="filled" icon="upload-simple" (click)="onAddUpload()">Upload a file</ph-button>
        </ph-empty-state>
    </ph-stack>

    <!-- ============================================================ -->
    <!-- DETAIL                                                        -->
    <!-- ============================================================ -->
    <ph-stack *ngSwitchCase="'detail'" class="skill-detail">
        <ph-link-button class="back-link" variant="gray" icon="arrow-left" size="small" (click)="backToList()">All skills</ph-link-button>

        <ph-skeleton *ngIf="detailLoading" shape="text" heading lines="5" label="Loading skill"></ph-skeleton>

        <ng-container *ngIf="!detailLoading && selected as skill">
            <ph-stack gap="tight">
                <ph-page-header dense size="small" [level]="3">
                    {{ skill.name }}
                    <ph-button slot="actions" variant="secondary" mode="filled" size="small" icon="pencil-simple" (click)="openEdit()">Edit</ph-button>
                    <ph-dropdown slot="actions" trigger-label="More" align="end" [items]="skillMenuItems" (ph-select)="onSkillMenuSelect($event.detail)"></ph-dropdown>
                </ph-page-header>
                <!-- The description is what Gable matches on, so it leads the page rather than hiding behind Edit. -->
                <div *ngIf="skill.description" data-test="skill-description">{{ skill.description }}</div>
                <ph-hint>By {{ skill.authorName }} · Updated {{ shortDate(skill.updatedAt) }}{{ skill.isEdited ? ' (edited)' : '' }}</ph-hint>
            </ph-stack>

            <ph-alert *ngIf="!skill.description" status="warning" variant="lighter" size="small">
                This skill has no description, so Gable can't match on it.
                <ph-link-button variant="primary" size="small" (click)="openEdit('#skillDescInput')">Add a description</ph-link-button>
            </ph-alert>

            <ph-list-row variant="card"
                         plain
                         wrap
                         [heading]="skill.enabled ? 'Turned on' : 'Turned off'"
                         [meta]="skill.enabled ? 'Gable brings this skill in when a conversation matches its description.' : 'Gable won\'t use this skill until you turn it back on.'">
                <ph-switch slot="actions" aria-label="Use this skill" [checked]="skill.enabled" (ph-change)="toggleEnabled()"></ph-switch>
            </ph-list-row>

            <ph-card *ngIf="isSuperUser && !skill.organizationId" flat heading="Applies to" action-label="Change" action-icon="pencil-simple" (ph-action)="openReachEditor('detail')">
                <ph-toolbar gap="tight">
                    <ph-tag *ngIf="skill.reach.mode !== 'selected'" variant="gray" icon="buildings">All organizations</ph-tag>
                    <ng-container *ngIf="skill.reach.mode === 'selected'">
                        <ph-tag *ngFor="let st of skill.reach.filters.states | slice:0:6; trackBy: trackByValue" variant="gray" icon="globe">{{ stateLabel(st) }}</ph-tag>
                        <ph-tag *ngIf="skill.reach.filters.states.length > 6" variant="stroke">+{{ skill.reach.filters.states.length - 6 }} more</ph-tag>
                        <ph-tag *ngFor="let b of skill.reach.filters.sizeBands; trackBy: trackByValue" variant="gray" icon="buildings">{{ bandLabel(b) }}</ph-tag>
                        <ph-tag *ngIf="!skill.reach.filters.states.length && !skill.reach.filters.sizeBands.length" variant="gray" icon="buildings">{{ skill.reachSummary }}</ph-tag>
                    </ng-container>
                    <ph-tag *ngFor="let ex of skill.reach.exceptions | slice:0:4; trackBy: trackByException" variant="stroke" [icon]="ex.type === 'always' ? 'check' : 'x'">{{ ex.type === 'always' ? 'Always' : 'Never' }}: {{ ex.name }}</ph-tag>
                    <ph-tag *ngIf="skill.reach.exceptions.length > 4" variant="stroke">+{{ skill.reach.exceptions.length - 4 }} more</ph-tag>
                </ph-toolbar>
            </ph-card>

            <ph-card flat heading="Instructions" action-label="Edit" action-icon="pencil-simple" (ph-action)="openEdit('#skillBodyInput')" *ngIf="skill.body">
                <ph-rich-text size="sm" [markdown]="playbookBody(skill.body)"></ph-rich-text>
            </ph-card>
        </ng-container>
    </ph-stack>

    <!-- ============================================================ -->
    <!-- EDIT                                                          -->
    <!-- ============================================================ -->
    <ph-stack *ngSwitchCase="'edit'" class="skill-edit">
        <ph-link-button class="back-link" variant="gray" icon="arrow-left" size="small" (click)="cancelEdit()">Cancel</ph-link-button>

        <!-- role=status: the banner reports what was parsed out of the dropped
             file, and it appears on a view switch a screen reader is otherwise
             given no signal about. -->
        <ph-alert *ngIf="edit.banner" status="information" variant="lighter" size="small" role="status">{{ edit.banner }}</ph-alert>

        <ph-field>
            <ph-label>Skill name</ph-label>
            <ph-input id="skillNameInput" [(ngModel)]="edit.name" ngDefaultControl maxlength="100"
                      placeholder="delinquency-escalation-ladder"></ph-input>
            <ph-hint>{{ fileNamePreview }}</ph-hint>
        </ph-field>

        <ph-field>
            <ph-label>Description</ph-label>
            <ph-textarea id="skillDescInput" rows="2" maxlength="1000" [(ngModel)]="edit.description"
                         ngDefaultControl placeholder="One sentence Gable can match this skill on."></ph-textarea>
        </ph-field>

        <ph-field>
            <ph-label>Instructions</ph-label>
            <ph-textarea id="skillBodyInput" rows="14" maxlength="65000" spellcheck="false"
                         [(ngModel)]="edit.body" ngDefaultControl></ph-textarea>
        </ph-field>

        <ng-container *ngIf="isSuperUser && !selected?.organizationId">
            <ph-divider></ph-divider>
            <ph-toolbar justify="between">
                <ph-stack gap="tight">
                    <ph-label>Applies to</ph-label>
                    <div>{{ editReachSummaryText }}</div>
                </ph-stack>
                <ph-link-button variant="primary" size="small" (click)="openReachEditor('edit')">Change</ph-link-button>
            </ph-toolbar>
        </ng-container>

        <ph-actions>
            <ph-button variant="secondary" mode="filled" (click)="cancelEdit()">Cancel</ph-button>
            <ph-button [disabled]="!canSaveEdit || saving" (click)="saveEdit()">{{ saving ? 'Saving…' : 'Save changes' }}</ph-button>
        </ph-actions>
    </ph-stack>

    <!-- ============================================================ -->
    <!-- ADD — upload a file                                           -->
    <!-- ============================================================ -->
    <ph-stack *ngSwitchCase="'add-upload'" class="skill-add-upload">
        <ph-link-button class="back-link" variant="gray" icon="arrow-left" size="small" (click)="view = 'list'">Add a skill</ph-link-button>
        <h3>Upload a file</h3>

        <ph-file-upload accept=".md" multiple
                        heading="Choose a file or drag &amp; drop it here"
                        hint="Markdown (.md) only — Gable reads the front matter for the skill's name and description."
                        action="Browse file"
                        (ph-files)="onUploadFilesSelected($event.detail)"></ph-file-upload>

        <ph-alert *ngIf="uploadError as message" status="error" variant="lighter" size="small" role="alert">{{ message }}</ph-alert>
    </ph-stack>

    <!-- ============================================================ -->
    <!-- ADD — write it with Gable                                     -->
    <!-- ============================================================ -->
    <ph-stack *ngSwitchCase="'add-gable'" class="skill-add-gable" [attr.aria-busy]="drafting ? 'true' : null">
        <ph-link-button class="back-link" variant="gray" icon="arrow-left" size="small" (click)="onLeaveDraft()">Add a skill</ph-link-button>
        <h3>Write it with Gable</h3>
        <ph-hint>
            Describe what you want in plain words — Gable will draft the name, description, and
            instructions for you to review.
        </ph-hint>
        <ph-textarea rows="6" maxlength="2000" [(ngModel)]="briefText" ngDefaultControl [readonly]="drafting"
                     aria-label="Describe the skill you want"
                     placeholder="e.g. When coding transactions, always check vendor history first…"></ph-textarea>

        <!-- The draft round-trips through an LLM, so it runs for seconds, not
             milliseconds. ph-spinner is its own aria-live region, so a screen
             reader hears this the same as a disabled button never could. -->
        <ph-spinner *ngIf="drafting" size="16" label="Gable is drafting your skill — this usually takes a few seconds."></ph-spinner> <!-- kitlint-allow-spinner: a wait with no layout to promise -->

        <ph-actions>
            <ph-button variant="secondary" mode="filled" (click)="onLeaveDraft()">Cancel</ph-button>
            <!-- gap-button-loading-state: ph-button has no busy state; disabled plus
                 the label swap is the only substitute today. -->
            <ph-button [disabled]="!briefText.trim() || drafting" (click)="onDraftWithGable()">{{ drafting ? 'Drafting…' : 'Draft it' }}</ph-button>
        </ph-actions>
    </ph-stack>

    <!-- ============================================================ -->
    <!-- ADD — browse the PayHOA library                               -->
    <!-- ============================================================ -->
    <ph-stack *ngSwitchCase="'add-library'" class="skill-add-library">
        <ph-link-button class="back-link" variant="gray" icon="arrow-left" size="small" (click)="view = 'list'">Add a skill</ph-link-button>
        <ph-stack gap="tight">
            <ph-page-header dense size="small" [level]="3">The PayHOA library</ph-page-header>
            <ph-hint>Skills PayHOA wrote for common work. Add one, then adjust it to fit.</ph-hint>
        </ph-stack>
        <ph-skeleton *ngIf="startersLoading && starters.length === 0" shape="list" rows="3" avatar="false" label="Loading the PayHOA library"></ph-skeleton>
        <ph-stack gap="snug" *ngIf="starters.length > 0">
            <ph-list-row *ngFor="let s of starters; trackBy: trackByStarterKey"
                         variant="card"
                         plain
                         wrap
                         [heading]="s.name"
                         [meta]="s.description">
                <ph-status-badge slot="actions" *ngIf="isStarterAdded(s); else addStarterButton" variant="light" status="completed">Added</ph-status-badge>
                <ng-template #addStarterButton>
                    <ph-button slot="actions" variant="secondary" mode="filled" size="small" icon="plus" [attr.aria-label]="'Add ' + s.name" (click)="addStarter(s)">Add</ph-button>
                </ng-template>
            </ph-list-row>
        </ph-stack>
    </ph-stack>

    <!-- ============================================================ -->
    <!-- REACH EDITOR                                                  -->
    <!-- ============================================================ -->
    <ph-stack *ngSwitchCase="'reach'" class="reach-editor">
        <ph-link-button class="back-link" variant="gray" icon="arrow-left" size="small" (click)="cancelReach()">Back</ph-link-button>
        <ph-stack gap="tight">
            <ph-page-header dense size="small" [level]="3">Who does this apply to?</ph-page-header>
            <ph-hint>Gable only brings this skill into conversations for the organizations it applies to.</ph-hint>
        </ph-stack>

        <ph-radio-group variant="card"
                        orientation="horizontal"
                        aria-label="Who this skill applies to"
                        [items]="reachModeItems"
                        [value]="reach.mode"
                        (ph-change)="setReachMode($event.detail)"></ph-radio-group>

        <ng-container *ngIf="reach.mode === 'selected'">
            <ph-stack gap="tight">
                <ph-label>Where are they?</ph-label>
                <ph-hint>All 50 states. Narrow it down only if the skill is state-specific.</ph-hint>

                <ph-toolbar *ngIf="reach.filters.states.length > 0">
                    <ph-tag *ngFor="let st of reach.filters.states; trackBy: trackByState" dismissible (ph-dismiss)="removeState(st)">
                        {{ stateLabel(st) }}
                    </ph-tag>
                </ph-toolbar>

                <ph-button *ngIf="!statesExpanded" variant="secondary" mode="filled" size="xsmall" icon="plus"
                           (click)="statesExpanded = true">Choose states</ph-button>

                <ph-card *ngIf="statesExpanded" flat>
                  <ph-stack gap="tight">
                    <ph-input placeholder="Search states" [(ngModel)]="stateSearch" ngDefaultControl aria-label="Search states"></ph-input>
                    <ph-scroll-area max-height="180px">
                        <ph-stack gap="tight">
                            <ph-field inline *ngFor="let st of filteredStates; trackBy: trackByState">
                                <ph-checkbox [checked]="isStateSelected(st.value)" (ph-change)="toggleState(st.value)" aria-label="{{ st.label }}"></ph-checkbox>
                                <ph-label (click)="toggleState(st.value)">{{ st.label }}</ph-label>
                            </ph-field>
                        </ph-stack>
                    </ph-scroll-area>
                    <ph-link-button variant="primary" size="small" (click)="statesExpanded = false">Done</ph-link-button>
                </ph-stack>
                </ph-card>
            </ph-stack>

            <ph-stack gap="tight">
                <ph-label>Which communities?</ph-label>
                <ph-toolbar>
                    <ph-chip [selected]="isEveryOrgBand()" [counter]="bandCounts['all']" (click)="setSizeAll()">Every organization</ph-chip>
                    <ph-chip [selected]="hasSizeBand('under_100')" [counter]="bandCounts['under_100']" (click)="toggleSizeBand('under_100')">Under 100 units</ph-chip>
                    <ph-chip [selected]="hasSizeBand('100_500')" [counter]="bandCounts['100_500']" (click)="toggleSizeBand('100_500')">100–500 units</ph-chip>
                    <ph-chip [selected]="hasSizeBand('over_500')" [counter]="bandCounts['over_500']" (click)="toggleSizeBand('over_500')">Over 500 units</ph-chip>
                    <ph-chip [selected]="reach.filters.customRange !== null" (click)="setSizeCustom()">Custom range</ph-chip>
                </ph-toolbar>
                <ph-toolbar *ngIf="reach.filters.customRange as range" nowrap>
                    <ph-input type="number" min="0" [(ngModel)]="range.min" (ngModelChange)="onReachChanged()" ngDefaultControl aria-label="Minimum units"></ph-input>
                    <span>to</span>
                    <ph-input type="number" min="0" [(ngModel)]="range.max" (ngModelChange)="onReachChanged()" ngDefaultControl aria-label="Maximum units"></ph-input>
                    <span>units</span>
                </ph-toolbar>
            </ph-stack>

            <ph-hint *ngIf="reachPreviewLoading">Counting organizations…</ph-hint>
            <ng-container *ngIf="!reachPreviewLoading && reachPreview as preview">
                <!-- A match count is a fact about the filters, not an alert; only an empty match is worth a warning. -->
                <ph-card flat *ngIf="preview.count > 0" data-test="reach-preview">
                    <ph-stack gap="tight">
                        <strong>{{ preview.count | number }} of {{ preview.total | number }} organizations</strong>
                        <ph-hint *ngIf="preview.examples.length > 0">Including {{ preview.examples.join(', ') }}</ph-hint>
                    </ph-stack>
                </ph-card>
                <ph-alert *ngIf="preview.count === 0" status="warning" variant="lighter" size="small">
                    <strong>No organizations match these filters</strong>
                    <ph-field inline>
                        <ph-checkbox [(ngModel)]="reachConfirmZero" ngDefaultControl aria-label="Save anyway"></ph-checkbox>
                        <ph-label>Save anyway</ph-label>
                    </ph-field>
                </ph-alert>
            </ng-container>

            <ph-stack gap="tight">
                <ph-label>Exceptions</ph-label>
                <ph-hint>
                    Exceptions stay put as organizations come and go. Everything else follows the filters,
                    so new communities that match are covered automatically.
                </ph-hint>
                <ph-input icon="magnifying-glass" placeholder="Search organizations" [(ngModel)]="exceptionSearch"
                          (ngModelChange)="onExceptionSearchChange($event)" ngDefaultControl
                          aria-label="Search organizations for an exception"></ph-input>
                <ph-stack *ngIf="exceptionResults.length > 0" gap="tight">
                    <ph-toolbar *ngFor="let org of exceptionResults; trackBy: trackByOrgLookup" justify="between">
                        <span>{{ org.name }}</span>
                        <ph-button *ngIf="org.matchesFilters" variant="secondary" mode="filled" size="xsmall" (click)="addException(org, 'never')">Exclude</ph-button>
                        <ph-button *ngIf="!org.matchesFilters" variant="secondary" mode="filled" size="xsmall" (click)="addException(org, 'always')">Include</ph-button>
                    </ph-toolbar>
                </ph-stack>
                <ph-toolbar *ngIf="reach.exceptions.length > 0">
                    <ph-tag *ngFor="let ex of reach.exceptions; trackBy: trackByException" dismissible (ph-dismiss)="removeException(ex)">
                        {{ ex.type === 'always' ? 'Always' : 'Never' }}: {{ ex.name }}
                    </ph-tag>
                </ph-toolbar>
            </ph-stack>
        </ng-container>

        <ph-actions>
            <ph-button variant="secondary" mode="filled" (click)="cancelReach()">Cancel</ph-button>
            <ph-button [disabled]="!canSaveReach" (click)="saveReach()">Save</ph-button>
        </ph-actions>
    </ph-stack>

    <input #replaceInput type="file" accept=".md" hidden aria-label="Replace skill file" (change)="onReplaceFileSelected($event)">
</ph-chat-gutter>

<app-confirm-dialog
    [open]="removeConfirmOpen"
    [heading]="'Remove &quot;' + (selected?.name ?? '') + '&quot;?'"
    message="Gable will stop using this skill immediately. This can't be undone from here."
    confirmLabel="Remove skill"
    [destructive]="true"
    (closed)="onRemoveConfirmClosed($event)"></app-confirm-dialog>
